/**
 * @file Controlled drug approval, reassignment, and submission feedback modals.
 */

import { state, appData } from '../../app/state/state.js';
import { closeForm, formModalElement } from '../../shared/forms/modalShell.js';
import { openConfirmationModal } from '../../shared/ui/confirmationModal.js';
import {
    formatDate,
} from "../../shared/utils/dateUtils.js";
import {
    isDrugsUiLocked,
    roundToPrecision,
} from "./drugUtils.js";
import {
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import { handleApprovalAction, reassignApproval } from './drugApi.js';
import {
    createSelectControl,
} from "../../shared/ui/controls/selectControl.js";
import {
    applyDrugControlSurface,
    bindDrugNativeFieldSurface,
    DRUG_APPROVE_BUTTON_CLASSES,
    DRUG_CANCEL_BUTTON_CLASSES,
    DRUG_CONTROL_HELPER_TEXT_CLASSES,
    DRUG_DANGER_BUTTON_CLASSES,
    DRUG_MODAL_AUTO_TEXTAREA_CLASSES,
    DRUG_SECONDARY_BUTTON_CLASSES,
    requireDrugActionFieldValue,
    validateDrugNativeFields,
} from "./drugControlStyles.js";

const REJECT_JUSTIFICATION_FIELD_ID = 'approval-reject-justification';
const REASSIGN_APPROVER_FIELD_ID = 'reassign-user-select';
const REASSIGN_APPROVER_CONTROL_ID = 'reassign-user-select-control';

async function rerenderCurrentBoard() {
    const { renderCurrentBoard } = await import('../../app/view/viewController.js');
    renderCurrentBoard();
}

function getConfirmationModalElement() {
    return document.getElementById('confirm-modal');
}

function applyDrugInterceptModalSurface() {
    const confirmModal = getConfirmationModalElement();
    applyDrugControlSurface(confirmModal, appData);
    bindDrugNativeFieldSurface(confirmModal);
    return confirmModal;
}

function getRejectJustificationMessage(isAuditApproval) {
    return isAuditApproval ?
        'Justification is required when rejecting an audit request.' :
        'Justification is required for rejected transactions.';
}

function openRejectJustificationModal(approvalId, isAuditApproval, onSuccess = null) {
    const validationMessage = getRejectJustificationMessage(isAuditApproval);
    const modalContent = `
        <div class="space-y-3 text-left">
            <p class="text-sm text-gray-600">Add the justification for rejecting this ${isAuditApproval ? 'audit request' : 'transaction'}.</p>
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Justification</label>
                <textarea id="${REJECT_JUSTIFICATION_FIELD_ID}" rows="1" class="${DRUG_MODAL_AUTO_TEXTAREA_CLASSES}" data-auto-grow="true" placeholder="Add a justification"></textarea>
            </div>
        </div>
    `;

    openConfirmationModal(
        'Reject Approval',
        modalContent,
        async () => {
            const scope = getConfirmationModalElement();
            const justificationField = document.getElementById(REJECT_JUSTIFICATION_FIELD_ID);
            if (!requireDrugActionFieldValue(scope, justificationField, validationMessage)) {
                return false;
            }

            const success = await handleApprovalAction(
                approvalId,
                'reject',
                justificationField.value.trim(),
            );
            if (success && onSuccess) {onSuccess();}
            return success;
        },
        true,
        'Reject',
        null,
        'Rejecting...',
        'Cancel',
        { keepOpenOnFalse: true, closeOnError: false },
    );

    applyDrugInterceptModalSurface();
}

function renderApprovalActionFooter(actionButtonsHtml) {
    const cancelBtn = document.getElementById('cancel-form-btn');
    const submitBtn = formModalElement?.querySelector('button[type="submit"]');
    const footer = cancelBtn?.parentElement || submitBtn?.parentElement || null;
    if (!footer || !cancelBtn || !submitBtn) {return false;}

    footer.querySelectorAll('[data-modal-custom-actions="true"]').forEach((element) => element.remove());
    footer.className = 'flex justify-between items-center gap-3 px-2 shrink-0';
    footer.style.display = '';
    cancelBtn.style.display = 'none';
    submitBtn.style.display = 'none';
    footer.insertAdjacentHTML('afterbegin', actionButtonsHtml);
    return true;
}

function tightenApprovalModalHeader() {
    const headerRow = formModalElement?.querySelector('#form-header-row');
    headerRow?.classList.remove('mb-6');
    headerRow?.classList.add('mb-3');
}

/**
 * Renders the approval review modal for drug workflows.
 * @param {string} approvalId Approval document ID.
 * @param {object} approvalData Approval document data.
 * @param {Function | null} onSuccess Optional callback after action success.
 * @returns {void}
 */
export function renderApprovalModal(approvalId, approvalData, onSuccess = null) {
    if (!formModalElement) {
        console.error('Form modal element not found');
        return;
    }

    const formTitle = formModalElement.querySelector('#form-title');
    const formHeaderActions = formModalElement.querySelector('#form-header-actions');
    const formFields = formModalElement.querySelector('#form-fields');
    const itemForm = formModalElement.querySelector('#item-form');
    const cancelBtn = document.getElementById('cancel-form-btn');
    const submitBtn = formModalElement.querySelector('button[type="submit"]');

    if (!formTitle || !formFields || !itemForm) {
        console.error('Required form elements not found');
        return;
    }

    const payload = approvalData.payload || {};
    const workflowType = approvalData.workflowType || '';
    const maintenanceLocked = isDrugsUiLocked();
    applyDrugControlSurface(formModalElement, appData);
    applyDrugControlSurface(itemForm, appData);
    if (formHeaderActions) {formHeaderActions.innerHTML = '';}
    tightenApprovalModalHeader();

    // Delegate to specific renderer for Audit workflows
    if (workflowType.startsWith('drug_audit_')) {
        renderAuditApprovalContent(approvalId, approvalData, formFields, formTitle, itemForm, onSuccess);
        formModalElement.classList.add('flex');
        return;
    }

    // Get user names
    const requester = appData.users?.find((u) => u.id === approvalData.requesterId);
    const requesterName = requester ? requester.name : 'Unknown User';

    // Format transaction details
    const drugNames = [];
    const locationNames = [];
    const quantities = [];

    if (payload.payload && Array.isArray(payload.payload)) {
        payload.payload.forEach((item) => {
            const drug = appData.drugInventory?.find((d) => d.id === item.drugId);
            if (drug) {drugNames.push(drug.name);}

            if (item.location) {
                const loc = appData.config?.drugs?.locations?.find((l) => l.id === item.location);
                locationNames.push(loc ? loc.name : item.location);
            }

            quantities.push(roundToPrecision(item.quantity));
        });
    }

    // Dynamic Title
    const typeLabel = (payload.type || '').replace('drug_', '').replace('_', ' ');
    const displayTitle = `Review ${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} Transaction`;
    formTitle.textContent = displayTitle;

    itemForm.dataset.type = 'drug_approval';
    itemForm.dataset.id = approvalId;
    itemForm.dataset.parentId = '';

    // Resolvers
    const providerName = payload.provider ? (appData.users?.find(u => u.id === payload.provider)?.name || 'Unknown') : null;

    // Build modal content
    let modalHtml = `
        <div class="space-y-4 pb-3">
            <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 class="font-semibold text-gray-900 dark:text-gray-100 mb-2">Transaction Details</h4>
                <div class="grid grid-cols-2 gap-2 text-sm">
                    <div><span class="text-gray-600 dark:text-gray-400">Type:</span> <span class="font-medium dark:text-gray-200">${sanitizeHTML(payload.type || 'Unknown')}</span></div>
                    <div><span class="text-gray-600 dark:text-gray-400">Date:</span> <span class="font-medium dark:text-gray-200">${sanitizeHTML(formatDate(payload.transactionDate) || '')}</span></div>
                    <div><span class="text-gray-600 dark:text-gray-400">Performed By:</span> <span class="font-medium dark:text-gray-200">${sanitizeHTML(requesterName)}</span></div>

                    ${payload.patientName ? `<div><span class="text-gray-600 dark:text-gray-400">Patient:</span> <span class="font-medium dark:text-gray-200">${sanitizeHTML(payload.patientName)}</span></div>` :
            (payload.batchCode ? `<div><span class="text-gray-600 dark:text-gray-400">Bulk Transaction:</span> <span class="font-medium dark:text-gray-200">${sanitizeHTML(payload.batchCode)}</span></div>` : '')}

                    ${/* Use / Relocate: Provider */ (payload.type === 'use' || payload.type === 'relocate') && providerName ?
            `<div><span class="text-gray-600 dark:text-gray-400">Provider:</span> <span class="font-medium dark:text-gray-200">${sanitizeHTML(providerName)}</span></div>` : ''}

                    ${/* Restock: Order Number */ (payload.type === 'restock' && payload.orderNumber) ?
            `<div><span class="text-gray-600 dark:text-gray-400">Order Number:</span> <span class="font-medium dark:text-gray-200">${sanitizeHTML(payload.orderNumber)}</span></div>` : ''}

                    ${/* Restock: Supplier */ (payload.type === 'restock' && payload.supplier) ? (() => {
            const supId = payload.supplier;
            const sup = appData.config?.drugs?.suppliers?.find(s => s.id === supId);
            const supName = sup ? sup.name : supId;
            return `<div><span class="text-gray-600 dark:text-gray-400">Supplier:</span> <span class="font-medium dark:text-gray-200">${sanitizeHTML(supName)}</span></div>`;
        })() : ''}

                    ${/* Correction / Loss: Justification */ (payload.type === 'correction' || payload.type === 'loss') && payload.justification ?
            `<div class="col-span-2 mt-2"><span class="text-gray-600 dark:text-gray-400 block mb-1">Justification:</span> <span class="font-medium dark:text-gray-200 italic p-2 bg-gray-100 dark:bg-gray-700 block rounded">${sanitizeHTML(payload.justification)}</span></div>` : ''}

                    ${/* Void: Reason */ (payload.type === 'void' || workflowType === 'drug_void_transaction') && (payload.voidReason || payload.justification) ?
            `<div class="col-span-2 mt-2"><span class="text-gray-600 dark:text-gray-400 block mb-1">Void Reason:</span> <span class="font-medium dark:text-gray-200 italic p-2 bg-gray-100 dark:bg-gray-700 block rounded">${sanitizeHTML(payload.voidReason || (payload.justification?.includes('Reason: ') ? payload.justification.split('Reason: ')[1] : payload.justification))}</span></div>` : ''}
                </div>
            </div>

            <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 class="font-semibold text-gray-900 dark:text-gray-100 mb-2">Drugs & Quantities</h4>
                <div class="space-y-2 text-sm">
    `;

    // Display items
    if (payload.payload && Array.isArray(payload.payload)) {
        const isRelocate = payload.type === 'relocate';
        const isVoid = payload.type === 'void' || workflowType === 'drug_void_transaction';

        if (isRelocate && payload.payload.length >= 2) {
            // Relocate Grouping (Same as History Tab)
            const drugGroups = {};
            payload.payload.forEach((item) => {
                if (!drugGroups[item.drugId]) {
                    drugGroups[item.drugId] = { source: null, dest: null };
                }
                if (item.quantity < 0) {drugGroups[item.drugId].source = item;}
                else {drugGroups[item.drugId].dest = item;}
            });

            Object.keys(drugGroups).forEach((drugId) => {
                const group = drugGroups[drugId];
                if (group.source && group.dest) {
                    const drug = appData.drugInventory?.find((d) => d.id === drugId);
                    const drugName = drug ? drug.name : 'Unknown Drug';
                    const fromLoc = appData.config?.drugs?.locations?.find((l) => l.id === group.source.location)?.name || group.source.location;
                    const toLoc = appData.config?.drugs?.locations?.find((l) => l.id === group.dest.location)?.name || group.dest.location;
                    const qty = Math.abs(group.source.quantity);

                    modalHtml += `
                        <div class="flex justify-between items-start py-2 border-b last:border-0 border-gray-100 dark:border-gray-700">
                            <div>
                                <div class="font-medium dark:text-gray-200">${sanitizeHTML(drugName)}</div>
                                <div class="text-xs text-gray-500 dark:text-gray-400 font-normal">${sanitizeHTML(fromLoc)} <span class="mx-1 text-gray-400">→</span> ${sanitizeHTML(toLoc)}</div>
                                ${group.source.justification ? `<div class="text-xs text-gray-500 dark:text-gray-400 italic mt-1 bg-gray-50 dark:bg-gray-700 p-1 rounded">Reason: ${sanitizeHTML(group.source.justification)}</div>` : ''}
                            </div>
                            <div class="font-bold whitespace-nowrap" style="color: #0080FE;">
                                ${roundToPrecision(qty)} amps
                            </div>
                        </div>
                    `;
                }
            });
        } else {
            // Standard Display (with Void styling)
            payload.payload.forEach((item, _idx) => {
                const drug = appData.drugInventory?.find((d) => d.id === item.drugId);
                const drugName = drug ? drug.name : 'Unknown Drug';
                const loc = item.location ? appData.config?.drugs?.locations?.find((l) => l.id === item.location) : null;
                const locName = loc ? loc.name : (item.location || 'N/A');
                const qty = roundToPrecision(item.quantity);

                let colorClass = qty < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';
                if (isVoid) {colorClass = 'text-gray-900 dark:text-gray-100';} // Black for voids

                // Breakdown text for use
                let breakdownHtml = '';
                if (payload.type === 'use' && (item.administered !== undefined || item.discarded !== undefined)) {
                    const admin = item.administered !== undefined ? item.administered : 0;
                    const disc = item.discarded !== undefined ? item.discarded : 0;
                    breakdownHtml = `<div class="flex-1 text-center text-xs text-gray-500 mx-2 mt-1">(Administered: ${admin}, Discarded: ${disc})</div>`;
                }

                modalHtml += `
                    <div class="flex justify-between items-start py-2 border-b last:border-0 border-gray-100 dark:border-gray-700">
                        <div>
                            <div class="font-medium dark:text-gray-200">${sanitizeHTML(drugName)} <span class="text-gray-600 dark:text-gray-400 font-normal text-sm">(${sanitizeHTML(locName)})</span></div>
                            ${item.justification ? `<div class="text-xs text-gray-500 dark:text-gray-400 italic mt-1 bg-gray-50 dark:bg-gray-700 p-1 rounded">Reason: ${sanitizeHTML(item.justification)}</div>` : ''}
                        </div>
                        ${breakdownHtml}
                        <div class="font-bold whitespace-nowrap ${colorClass}">
                            ${qty > 0 ? '+' : ''}${qty} amps
                        </div>
                    </div>
                `;
            });
        }
    }

    modalHtml += `
                </div>
            </div>
        </div>
    `;

    formFields.innerHTML = modalHtml;

    // Cleanup leftover bulk checkbox from drug forms
    const existingBulk = formTitle.parentElement?.querySelector('#bulk-checkbox-container');
    if (existingBulk) {existingBulk.remove();}

    // Add action buttons in the real modal footer so no empty default footer remains below them.
    const actionButtonsHtml = `
        <div class="flex justify-between items-center w-full" id="approval-action-buttons" data-modal-custom-actions="true">
             <div class="flex space-x-3">
                 <button type="button" id="btn-approval-cancel" class="${DRUG_CANCEL_BUTTON_CLASSES}">Close</button>
                 <button type="button" id="btn-approval-reassign" class="${DRUG_SECONDARY_BUTTON_CLASSES}" ${maintenanceLocked ? 'disabled' : ''}><span class="material-symbols-outlined text-sm">person_add</span> Reassign</button>
             </div>
            <div class="flex space-x-3">
                <button type="button" id="btn-approve-reject" class="${DRUG_DANGER_BUTTON_CLASSES}" ${maintenanceLocked ? 'disabled' : ''}>Reject</button>
                <button type="button" id="btn-approve" class="${DRUG_APPROVE_BUTTON_CLASSES}" ${maintenanceLocked ? 'disabled' : ''}>Approve</button>
            </div>
        </div>
        `;
    if (!renderApprovalActionFooter(actionButtonsHtml)) {
        if (cancelBtn) {cancelBtn.style.display = 'none';}
        if (submitBtn) {submitBtn.style.display = 'none';}
        formFields.insertAdjacentHTML('beforeend', actionButtonsHtml);
    }

    if (maintenanceLocked) {
        formFields.insertAdjacentHTML('beforeend', `
            <div class="mt-4 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                Drugs system locked for maintenance. Approval actions are temporarily disabled.
            </div>
        `);
    }
    bindDrugNativeFieldSurface(itemForm);

    // Bind new Cancel button
    const newCancelBtn = document.getElementById('btn-approval-cancel');
    if (newCancelBtn) {newCancelBtn.onclick = closeForm;}

    // Bind Reassign button
    const reassignBtn = document.getElementById('btn-approval-reassign');
    if (reassignBtn) {
        reassignBtn.onclick = () => {
            openReassignModal(approvalId, approvalData);
        };
    }

    // Action button handlers
    const approveBtn = document.getElementById('btn-approve');
    const rejectBtn = document.getElementById('btn-approve-reject');
    const modalCancelBtn = document.getElementById('btn-approval-cancel');

    let isProcessingApprovalAction = false;
    const actionButtons = [approveBtn, rejectBtn, modalCancelBtn].filter(Boolean);
    const defaultButtonLabels = new Map(
        actionButtons.map((button) => [button, button.textContent]),
    );

    const setApprovalActionState = (isProcessing, activeButton = null) => {
        isProcessingApprovalAction = isProcessing;
        actionButtons.forEach((button) => {
            button.disabled = isProcessing;
            button.classList.toggle('opacity-60', isProcessing);
            button.classList.toggle('cursor-not-allowed', isProcessing);
            button.textContent = defaultButtonLabels.get(button);
        });
        if (isProcessing && activeButton) {
            activeButton.textContent = 'Processing...';
        }
    };

    if (approveBtn) {
        approveBtn.onclick = async () => {
            if (maintenanceLocked) {return;}
            if (isProcessingApprovalAction) {return;}
            setApprovalActionState(true, approveBtn);
            try {
                const success = await handleApprovalAction(approvalId, 'approve', null);
                if (!success) {
                    setApprovalActionState(false);
                }
            } catch (error) { void error;
                setApprovalActionState(false);
            }
        };
    }

    if (rejectBtn) {
        rejectBtn.onclick = async () => {
            if (maintenanceLocked) {return;}
            if (isProcessingApprovalAction) {return;}
            openRejectJustificationModal(approvalId, false);
        };
    }

    formModalElement.classList.add('flex');
}

function renderAuditApprovalContent(approvalId, approvalData, formFields, formTitle, itemForm, onSuccess) {
    const payload = approvalData.payload || {};
    const maintenanceLocked = isDrugsUiLocked();
    const requester = appData.users?.find(u => u.id === approvalData.requesterId);
    const requesterName = requester ? requester.name : 'Unknown User';
    // Use transactionDate from payload, or createdAt from approval
    const dateStr = payload.transactionDate || approvalData.createdAt;
    const dateDisplay = formatDate(dateStr);

    let title = 'Review Audit Request';
    let bodyHtml = '';

    // Hide default buttons
    const cancelBtn = document.getElementById('cancel-form-btn');
    const submitBtn = formModalElement.querySelector('button[type="submit"]');
    if (cancelBtn) {cancelBtn.style.display = 'none';}
    if (submitBtn) {submitBtn.style.display = 'none';}
    tightenApprovalModalHeader();

    itemForm.dataset.type = 'audit_approval';
    itemForm.dataset.id = approvalId;
    applyDrugControlSurface(formModalElement, appData);
    applyDrugControlSurface(itemForm, appData);

    if (approvalData.workflowType === 'drug_audit_start') {
        title = 'Review Audit Start Request';
        bodyHtml = `
            <div class="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg text-center">
                <div class="mb-4">
                    <span class="material-symbols-outlined text-5xl text-blue-500">fact_check</span>
                </div>
                <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">New Audit Request</h3>
                <p class="text-gray-600 dark:text-gray-400 mb-4">
                    <strong>${sanitizeHTML(requesterName)}</strong> has requested to <span class="text-blue-600 font-bold">START</span> a new Controlled Drugs Audit.
                </p>
                <div class="inline-block bg-white dark:bg-gray-700 px-4 py-2 rounded border border-gray-200 dark:border-gray-600">
                    <span class="text-sm text-gray-500 dark:text-gray-400">Requested Date</span><br>
                    <span class="font-bold text-gray-800 dark:text-gray-200">${dateDisplay}</span>
                </div>
            </div>
        `;
    } else if (approvalData.workflowType === 'drug_audit_cancel') {
        title = 'Review Audit Cancellation';
        const reason = payload.reason || 'No reason provided';
        bodyHtml = `
            <div class="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg text-center">
                 <div class="mb-4">
                    <span class="material-symbols-outlined text-5xl text-red-500">cancel_presentation</span>
                </div>
                <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Audit Cancellation Request</h3>
                <p class="text-gray-600 dark:text-gray-400 mb-4">
                    <strong>${sanitizeHTML(requesterName)}</strong> has requested to <span class="text-red-600 font-bold">CANCEL</span> the active audit.
                </p>
                <div class="bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600 text-left mx-auto max-w-sm">
                    <span class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Reason</span>
                    <p class="text-gray-800 dark:text-gray-200 mt-1 italic">"${sanitizeHTML(reason)}"</p>
                </div>
            </div>
        `;
    } else if (approvalData.workflowType === 'drug_audit_finalize') {
        title = 'Review Audit Finalisation';
        const resultStatus = payload.resultsStatus || 'unknown';
        const isReconciled = resultStatus === 'reconciled';
        const resultColor = isReconciled ? 'text-green-600' : 'text-red-600';
        const resultIcon = isReconciled ? 'check_circle' : 'warning';

        bodyHtml = `
             <div class="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg text-center">
                 <div class="mb-4">
                    <span class="material-symbols-outlined text-5xl ${isReconciled ? 'text-green-500' : 'text-red-500'}">assignment_turned_in</span>
                </div>
                <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Audit Finalisation Request</h3>
                <p class="text-gray-600 dark:text-gray-400 mb-4">
                    <strong>${sanitizeHTML(requesterName)}</strong> has requested to <span class="font-bold">FINALISE</span> the active audit.
                </p>

                <div class="flex justify-center gap-4 mb-4">
                     <div class="text-center">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Requested Date</span><br>
                        <span class="font-medium text-gray-800 dark:text-gray-200">${dateDisplay}</span>
                    </div>
                    <div class="text-center border-l pl-4 dark:border-gray-600">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Result</span><br>
                        <span class="font-bold ${resultColor} flex items-center justify-center gap-1">
                            <span class="material-symbols-outlined text-sm">${resultIcon}</span>
                            ${resultStatus === 'reconciled' ? 'Reconciled' : 'Discrepancy'}
                        </span>
                    </div>
                </div>

                ${!isReconciled ? `<div class="text-xs text-gray-500 dark:text-gray-400 italic">Review full audit details in the Audit Log if needed.</div>` : ''}
            </div>
        `;
    }

    formTitle.textContent = title;

    const maintenanceNotice = maintenanceLocked ? `
        <div class="mt-4 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            Drugs system locked for maintenance. Approval actions are temporarily disabled.
        </div>
    ` : '';

    // Action Buttons
    const actionButtonsHtml = `
        <div class="flex justify-between items-center w-full" id="approval-action-buttons" data-modal-custom-actions="true">
             <button type="button" id="btn-approval-cancel" class="${DRUG_CANCEL_BUTTON_CLASSES}">Close</button>
            <div class="flex space-x-3">
                <button type="button" id="btn-approve-reject" class="${DRUG_DANGER_BUTTON_CLASSES}" ${maintenanceLocked ? 'disabled' : ''}>Reject</button>
                <button type="button" id="btn-approve" class="${DRUG_APPROVE_BUTTON_CLASSES}" ${maintenanceLocked ? 'disabled' : ''}>Approve</button>
            </div>
        </div>
    `;

    formFields.innerHTML = `<div class="pb-3">${bodyHtml}${maintenanceNotice}</div>`;
    if (!renderApprovalActionFooter(actionButtonsHtml)) {
        if (cancelBtn) {cancelBtn.style.display = 'none';}
        if (submitBtn) {submitBtn.style.display = 'none';}
        formFields.insertAdjacentHTML('beforeend', actionButtonsHtml);
    }
    bindDrugNativeFieldSurface(itemForm);

    // Bind Events
    const newCancelBtn = document.getElementById('btn-approval-cancel');
    if (newCancelBtn) {newCancelBtn.onclick = closeForm;}

    const approveBtn = document.getElementById('btn-approve');
    const rejectBtn = document.getElementById('btn-approve-reject');
    const actionButtons = [newCancelBtn, approveBtn, rejectBtn].filter(Boolean);
    const defaultButtonLabels = new Map(
        actionButtons.map((button) => [button, button.textContent]),
    );
    let isProcessingApprovalAction = false;

    const setApprovalActionState = (isProcessing, activeButton = null) => {
        isProcessingApprovalAction = isProcessing;
        actionButtons.forEach((button) => {
            button.disabled = isProcessing;
            button.classList.toggle('opacity-60', isProcessing);
            button.classList.toggle('cursor-not-allowed', isProcessing);
            button.textContent = defaultButtonLabels.get(button);
        });
        if (isProcessing && activeButton) {
            activeButton.textContent = 'Processing...';
        }
    };

    if (approveBtn) {
        approveBtn.onclick = async () => {
            if (maintenanceLocked) {return;}
            if (isProcessingApprovalAction) {return;}
            setApprovalActionState(true, approveBtn);
            try {
                const success = await handleApprovalAction(approvalId, 'approve', null);
                if (success && onSuccess) {
                    onSuccess();
                } else if (!success) {
                    setApprovalActionState(false);
                }
            } catch (error) { void error;
                setApprovalActionState(false);
            }
        };
    }

    if (rejectBtn) {
        rejectBtn.onclick = async () => {
            if (maintenanceLocked) {return;}
            if (isProcessingApprovalAction) {return;}
            openRejectJustificationModal(approvalId, true, onSuccess);
        };
    }

}

/**
 * Shows post-submission approval feedback for drug transactions and audits.
 * @param {object} response Callable response from the drug workflow.
 * @param {string} transactionType Transaction or audit request type.
 * @returns {void}
 */
export function renderSubmissionFeedback(response, transactionType) {
    const cleanType = (transactionType || '').replace(/_/g, ' ');
    const typeLabel = cleanType ?
        cleanType.charAt(0).toUpperCase() + cleanType.slice(1) :
        'Transaction';

    let title;
    let message;
    const details = response.approverDetails;

    // Helper to format approver list
    const formatApprovers = (approverDetails) => {
        if (!approverDetails || approverDetails.length === 0) {return '';}
        let listHtml = '<ul class="list-disc pl-5 mt-2 text-sm text-gray-600 dark:text-gray-400">';
        approverDetails.forEach(detail => {
            const role = detail.roleName ? `<strong>${detail.roleName}</strong>: ` : '';
            const names = detail.names.join(', ');
            listHtml += `<li>${role}${names}</li>`;
        });
        listHtml += '</ul>';
        return listHtml;
    };

    const suffix = cleanType.toLowerCase().includes('audit') ? 'request' : 'transaction';

    switch (response.approvalMode) {
        case 'bypass':
        case 'automatic_approval':
            title = 'Request Approved';
            message = `Your ${typeLabel} ${suffix} has been processed and automatically approved.`;
            break;

        case 'post_action': // Soft Block
            title = 'Pending Final Approval';
            message = `Your ${typeLabel} ${suffix} has been processed and submitted for final approval.`;
            if (details && details.length > 0) {
                message += `<br><br><strong>Approvers:</strong>${formatApprovers(details)}`;
            }
            break;

        case 'pre_action': // Hard Block
            title = 'Approval Required';
            message = `Your ${typeLabel} ${suffix} has been submitted for approval.`;
            if (details && details.length > 0) {
                message += `<br><br><strong>Approvers:</strong>${formatApprovers(details)}`;
            }
            break;

        default:
            title = 'Success';
            message = response.message || `${typeLabel} processed successfully.`;
    }

    // Force immediate re-render if needed (e.g. for audit console update)
    // Use setTimeout to allow Firestore listener to update appData first
    setTimeout(() => {
        void rerenderCurrentBoard();
    }, 100);

    openConfirmationModal(
        title,
        message,
        () => {
            // Optional: Callback after closing feedback
            void rerenderCurrentBoard();
        },
        false,
        'OK'
    );
}

function openReassignModal(approvalId, approvalData) {
    if (!formModalElement) {return;}
    const maintenanceLocked = isDrugsUiLocked();
    applyDrugControlSurface(formModalElement, appData);

    if (maintenanceLocked) {
        openConfirmationModal(
            'Reassign Approval',
            `
            <div class="text-center p-6 text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg">
                <span class="material-symbols-outlined text-4xl mb-2">lock</span>
                <p class="font-medium">Drugs system locked for maintenance.</p>
                <p class="text-sm mt-2">Approval reassignment is temporarily disabled.</p>
            </div>
            `,
            () => { },
            false,
            'OK',
        );
        applyDrugInterceptModalSurface();
        return;
    }

    // 1. Calculate Eligible Users
    const currentRole = approvalData.currentApproverRole;
    const escalationPath = approvalData.escalationPath || [];
    const requesterId = approvalData.requesterId;
    const currentUid = state.currentUser?.uid;
    const allUsers = appData.users || [];

    // Determine Base Role (Floor)
    // Use originalApproverRole if available, otherwise assume lowest rank (last item)
    const originalRole = approvalData.originalApproverRole ||
        (escalationPath.length > 0 ? escalationPath[escalationPath.length - 1] : null);

    // Index 0 = Top Rank. High Index = Lower Rank.
    const originalIndex = escalationPath.indexOf(originalRole);

    const eligibleUsers = allUsers.filter(user => {
        if (user.id === currentUid) {return false;} // Not self
        if (user.id === requesterId) {return false;} // Not requester
        if (user.isActive === false) {return false;} // Not inactive
        if (!user.drugRole) {return false;} // Must have a role

        // 1. Peer Check (Always allow lateral to CURRENT role)
        if (user.drugRole === currentRole) {return true;}

        // 2. Escalation Scope Check
        // Allow anyone who is in the path AND is "Higher or Equal" to the Original Request level.
        // i.e., their index must be <= originalIndex.
        const userIndex = escalationPath.indexOf(user.drugRole);

        if (userIndex !== -1) {
            if (originalIndex === -1) {return true;} // Safety: If base unknown, allow all path members
            return userIndex <= originalIndex;
        }

        return false;
    });

    // 2. Group and Sort Users
    // Group by Role
    const groupedUsers = {};
    eligibleUsers.forEach(u => {
        const roleId = u.drugRole || 'Unknown Role';
        // Try to get name from user object OR lookup in appData
        let roleName = u.drugRoleName;
        if (!roleName && appData.drugRoles) {
            const matchedRole = appData.drugRoles.find((roleDef) => roleDef.id === roleId);
            roleName = matchedRole ? matchedRole.name : roleId;
        }
        if (!roleName) {roleName = roleId;}

        // Clean up snake_case if fallback (e.g. role_admin -> Role Admin)
        if (roleName.includes('_')) {
            roleName = roleName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }

        if (!groupedUsers[roleName]) {groupedUsers[roleName] = [];}
        groupedUsers[roleName].push(u);
    });

    // Sort Groups by Escalation Path Order (Rank)
    const sortedGroupNames = Object.keys(groupedUsers).sort((aName, bName) => {
        // Find representative user to get role ID for path comparison
        const roleIdA = groupedUsers[aName][0]?.drugRole;
        const roleIdB = groupedUsers[bName][0]?.drugRole;
        const indexA = escalationPath.indexOf(roleIdA);
        const indexB = escalationPath.indexOf(roleIdB);
        // If not in path (unexpected), push to bottom
        if (indexA === -1) {return 1;}
        if (indexB === -1) {return -1;}
        return indexA - indexB; // Lower index = Higher Rank = Top of list
    });

    if (eligibleUsers.length === 0) {
        openConfirmationModal(
            'Reassign Approval',
            `
            <div class="text-center p-6 text-gray-500 dark:text-gray-400">
                <span class="material-symbols-outlined text-4xl mb-2">person_off</span>
                <p>No eligible users found to reassign this request to.</p>
                <p class="text-xs mt-2">(Must be a Peer or Superior, excluding the requester)</p>
            </div>
            `,
            () => { },
            false,
            'OK',
        );
        applyDrugInterceptModalSurface();
        return;
    }

    const reassignUserOptions = [];

    sortedGroupNames.forEach(roleName => {
        // Sort users within group by Admin Sort Order (drugSortOrder)
        const usersInGroup = groupedUsers[roleName].sort((a, b) => {
            const orderA = a.drugSortOrder !== undefined && a.drugSortOrder !== null ? Number(a.drugSortOrder) : 999;
            const orderB = b.drugSortOrder !== undefined && b.drugSortOrder !== null ? Number(b.drugSortOrder) : 999;
            if (orderA !== orderB) {return orderA - orderB;}
            return a.name.localeCompare(b.name);
        });

        usersInGroup.forEach(u => {
            reassignUserOptions.push({
                value: u.id,
                label: u.name,
                group: roleName,
                order: u.drugSortOrder !== undefined && u.drugSortOrder !== null ? Number(u.drugSortOrder) : 999,
            });
        });
    });

    let reassignSelectControl = null;
    const modalContent = `
        <div class="space-y-3 text-left">
            <label class="block text-sm font-medium text-gray-700">Select New Approver</label>
            <input type="hidden" id="${REASSIGN_APPROVER_FIELD_ID}" name="${REASSIGN_APPROVER_FIELD_ID}" required>
            <div id="${REASSIGN_APPROVER_CONTROL_ID}"></div>
            <p class="${DRUG_CONTROL_HELPER_TEXT_CLASSES}">
                Reassigning will transfer this approval request to the selected user.
                They will be notified immediately.
            </p>
        </div>
    `;

    openConfirmationModal(
        'Reassign Approval',
        modalContent,
        async () => {
            const confirmModal = getConfirmationModalElement();
            const select = document.getElementById(REASSIGN_APPROVER_FIELD_ID);
            const newApproverId = select?.value || '';
            if (!validateDrugNativeFields(confirmModal) || !newApproverId) {
                reassignSelectControl?.open();
                return false;
            }

            try {
                const result = await reassignApproval(approvalId, newApproverId);
                if (result && result.success) {
                    reassignSelectControl?.destroy();
                    closeForm();
                    setTimeout(() => {
                        openConfirmationModal('Success', 'Approval reassigned successfully.', () => { }, false, 'OK');
                    }, 0);
                    return true;
                }
            } catch (err) { void err; }
            return false;
        },
        false,
        'Confirm Reassignment',
        () => {
            reassignSelectControl?.destroy();
        },
        'Processing...',
        'Cancel',
        { keepOpenOnFalse: true, closeOnError: false },
    );

    const confirmModal = applyDrugInterceptModalSurface();
    const reassignControlRoot = document.getElementById(REASSIGN_APPROVER_CONTROL_ID);
    const reassignMirror = document.getElementById(REASSIGN_APPROVER_FIELD_ID);
    if (reassignControlRoot && reassignMirror) {
        reassignSelectControl = createSelectControl({
            root: reassignControlRoot,
            mirrorInput: reassignMirror,
            options: reassignUserOptions,
            placeholder: 'Select user',
            accessibleName: 'Select new approver',
            searchable: true,
            searchThreshold: 7,
            required: true,
            requiredMessage: 'Select a new approver',
            portalMenu: true,
        });
    }
    bindDrugNativeFieldSurface(confirmModal);
}
